import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import moment from "moment";
import {CircularProgress} from "@mui/material";

const formatTime = (start, end) => {
  const startMoment = moment(start);
  const endMoment = moment(end);

  const formattedStartTime = startMoment.format('dddd, MMM. DD, YYYY, h A');
  const formattedEndTime = endMoment.format('h A');

  return `${formattedStartTime} – ${formattedEndTime}`;
};

const improveColorReadability = (colorHex) => {
  // Remove the hash if it exists
  colorHex = colorHex.replace('#', '');

  // Convert Hex to RGB
  const r = parseInt(colorHex.substring(0, 2), 16);
  const g = parseInt(colorHex.substring(2, 4), 16);
  const b = parseInt(colorHex.substring(4, 6), 16);

  // Convert RGB to HSL
  let r1 = r / 255;
  let g1 = g / 255;
  let b1 = b / 255;
  const maxColor = Math.max(r1, g1, b1);
  const minColor = Math.min(r1, g1, b1);

  // Calculate L:
  let L = (maxColor + minColor) / 2 ;
  let S = 0;
  let H = 0;
  if (maxColor !== minColor) {
    if (L < 0.5) {
      S = (maxColor - minColor) / (maxColor + minColor);
    } else {
      S = (maxColor - minColor) / (2.0 - maxColor - minColor);
    }

    if (r1 === maxColor) {
      H = (g1 - b1) / (maxColor - minColor);
    } else if (g1 === maxColor) {
      H = 2.0 + (b1 - r1) / (maxColor - minColor);
    } else {
      H = 4.0 + (r1 - g1) / (maxColor - minColor);
    }
  }

  H = Math.floor(H * 60);
  S = Math.floor(S * 100);
  L = Math.floor(L * 100);

  // Increase the lightness
  if (L < 50) {
    L = 70;
  } else {
    L = Math.min(100, L);
  }

  // Convert HSL back to RGB
  H /= 360;
  S /= 100;
  L /= 100;
  let r2, g2, b2;

  if (S === 0) {
    r2 = g2 = b2 = L; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
    const p = 2 * L - q;

    r2 = hue2rgb(p, q, H + 1 / 3);
    g2 = hue2rgb(p, q, H);
    b2 = hue2rgb(p, q, H - 1 / 3);
  }

  r2 = Math.round(r2 * 255);
  g2 = Math.round(g2 * 255);
  b2 = Math.round(b2 * 255);

  // Convert RGB back to Hex
  const rHex = ("0" + r2.toString(16)).slice(-2);
  const gHex = ("0" + g2.toString(16)).slice(-2);
  const bHex = ("0" + b2.toString(16)).slice(-2);

  return `#${rHex}${gHex}${bHex}`;
};


const EventComponent = () => {
  const [events, setEvents] = useState([]);
  const [startDate, setStartDate] = useState(new Date()); // Defaults to today
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // Defaults to one week from today
  const [colorFilter, setColorFilter] = useState("#51A47C");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true)
      const start = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      const apiUrl = `https://www.lexingtonky.gov/calendar/fetchEventsAndMeetings?start=${start}&end=${end}`;
      try {
        const response = await axios.post('https://corded-well-397714.uc.r.appspot.com/events', { url: apiUrl });
        setEvents(response.data || []);
        setLoading(false)
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false)
      }
    };


    fetchEvents();
  }, [startDate, endDate]);



  return (
    <Box p={4}>
      <FormControl variant="outlined" style={{ marginBottom: '16px' }}>
        <InputLabel id="color-filter-label">Filter By</InputLabel>
        <Select
          labelId="color-filter-label"
          value={colorFilter ?? ""}
          onChange={e => setColorFilter(e.target.value)}
          label="Filter By"
        >
          <MenuItem value={"#51A47C"}>Public Meetings</MenuItem>
          <MenuItem value={"#004585"}>Activities</MenuItem>
          <MenuItem value={"All"}>All Events</MenuItem>
        </Select>
      </FormControl>
      <Grid container spacing={3} alignItems="center">
        <Grid item>
          <Typography variant="h5">Select Start Date:</Typography>
        </Grid>
        <Grid item>
          <TextField
            type="date"
            variant="outlined"
            value={startDate.toISOString().substr(0, 10)}
            onChange={e => setStartDate(new Date(e.target.value))}
          />
        </Grid>
        <Grid item>
          <Typography variant="h5">Select End Date:</Typography>
        </Grid>
        <Grid item>
          <TextField
            type="date"
            variant="outlined"
            value={endDate.toISOString().substr(0, 10)}
            onChange={e => setEndDate(new Date(e.target.value))}
          />
        </Grid>
      </Grid>

      <Typography variant="h4" gutterBottom>
        Upcoming LFUCG* Calendar Events
      </Typography>
      <Typography>
        *Not authorized by LFUCG.
      </Typography>
      {loading && <CircularProgress/>}
      {events.filter(event => {
        if (colorFilter.includes('#')) {
          return event.color === colorFilter;
        }
        return true;
      }).map((event, index) => (
        <Paper elevation={3} key={index} style={{ backgroundColor: improveColorReadability(event.color || '#f9f9f9'), padding: '16px', marginBottom: '16px' }}>
          <Typography variant="h6">
            <a href={`https://lexingtonky.gov/${event.url}` || '#'} target="_blank" rel="noreferrer">
              {event.title}
            </a>
          </Typography>
          <Typography variant="subtitle1">{formatTime(event.start, event.end)}</Typography>
          <div dangerouslySetInnerHTML={{ __html: event.description }}></div>
          <Box mt={2}>
            {event.relatedDepartments && <Chip label={event.relatedDepartments} variant="filled"/>}
            {event.relatedPages && <Chip label={event.relatedPages} variant="filled" style={{marginLeft: '8px'}}/>}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default EventComponent;
